import React from 'react';
import { perfTone, Sparkline } from '../../shared';
import type { EmployeeView } from '../../shared';
import { Panel, PanelHeader } from './PanelHeader.js';

interface PerformancePanelProps {
  employee: EmployeeView;
  reliabilitySeries?: number[];
  passRateSeries?: number[];
  latencySeries?: number[];
  costSeries?: number[];
}

export const PerformancePanel: React.FC<PerformancePanelProps> = ({
  employee,
  reliabilitySeries,
  passRateSeries,
  latencySeries,
  costSeries,
}) => {
  const reliability = reliabilitySeries ?? synthSeries(employee.reliability);
  const passRate = passRateSeries ?? synthSeries(employee.passRate);
  const latency = latencySeries ?? synthSeries(employee.latencyMs / 1000, 0.6);
  const cost = costSeries ?? synthSeries(employee.cost7d / 3, 0.6);
  const tone = perfTone(employee.passRate);
  const passColor = tone === 'bad' ? 'var(--danger)' : tone === 'warn' ? 'var(--warning)' : 'var(--success)';
  return (
    <Panel>
      <PanelHeader
        title="Performance"
        subtitle="Last 7 days · reliability, quality, latency, cost"
        right={<span className="font-mono text-[10px] uppercase tracking-wider text-faint">30d</span>}
      />
      <div className="grid grid-cols-1 gap-px bg-border md:grid-cols-2 lg:grid-cols-4">
        <PerfCell
          label="Reliability"
          value={`${Math.round(employee.reliability * 100)}%`}
          delta="+2% vs last wk"
          deltaDir="up"
          points={reliability}
          color="var(--success)"
        />
        <PerfCell
          label="Review pass"
          value={`${Math.round(employee.passRate * 100)}%`}
          delta={employee.passRate >= 0.9 ? '+1% vs last wk' : '−3% vs last wk'}
          deltaDir={employee.passRate >= 0.9 ? 'up' : 'down'}
          points={passRate}
          color={passColor}
        />
        <PerfCell
          label="Avg latency"
          value={employee.latencyMs ? `${(employee.latencyMs / 1000).toFixed(1)}s` : '—'}
          delta="+12% vs last wk"
          deltaDir="down"
          points={latency}
          color="var(--warning)"
        />
        <PerfCell
          label="Cost 7d"
          value={`$${employee.cost7d.toFixed(2)}`}
          delta={`${employee.cost7dTrend >= 0 ? '+' : ''}${Math.round(employee.cost7dTrend * 100)}% vs last wk`}
          deltaDir={employee.cost7dTrend > 0 ? 'down' : 'up'}
          points={cost}
          color="var(--accent)"
        />
      </div>
    </Panel>
  );
};

const PerfCell: React.FC<{
  label: string;
  value: string;
  delta: string;
  deltaDir: 'up' | 'down';
  points: number[];
  color: string;
}> = ({ label, value, delta, deltaDir, points, color }) => (
  <div className="bg-surface p-4">
    <div className="font-mono text-[10px] uppercase tracking-wider text-faint">{label}</div>
    <div className="mt-1.5 font-mono text-2xl font-semibold leading-none text-heading">{value}</div>
    <div className={`mt-1 text-xs ${deltaDir === 'up' ? 'text-success' : 'text-danger'}`}>{delta}</div>
    <div className="mt-2">
      <Sparkline points={points} color={color} width={132} height={28} ariaLabel={`${label} trend`} />
    </div>
  </div>
);

function synthSeries(end: number, jitter = 0.1): number[] {
  if (!Number.isFinite(end) || end <= 0) return [end];
  const base = Math.max(end * (1 - jitter), 0);
  return [
    base + (end - base) * 0.1,
    base + (end - base) * 0.3,
    base + (end - base) * 0.45,
    base + (end - base) * 0.6,
    base + (end - base) * 0.75,
    base + (end - base) * 0.9,
    end,
  ];
}
